import { DestinationFaq, FaqGroup, FaqEntry, FaqIntent } from '../types/faq.types';

interface FetchFaqParams {
    brand: string;
    tenant: string;
    currency: string;
    lang: string;
    identifier: string;
    travelcontext?: string;
}

interface ApiResponse {
    results?: number;
    title?: string;
    intro?: string;
    faqs?: Array<{
        id: string;
        question: string;
        answer: string;
    }>;
    meta?: {
        service: string;
        version: string;
        buildTime: string;
    };
    error?: string;
    missing?: string[];
    invalid?: string[];
}

/**
 * Service for loading FAQ data via midlayer
 */
export class FaqService {
    /**
     * Fetch FAQ data from midlayer Azure Function
     * @param params - Request parameters including brand, tenant, currency, lang, identifier, travelcontext
     * @returns Promise with DestinationFaq
     */
    static async fetchFaq(params: FetchFaqParams): Promise<DestinationFaq> {
        const { brand, tenant, currency, lang, identifier, travelcontext } = params;
        const url = new URL(import.meta.env.VITE_SERVICE_URL);
        url.searchParams.set('brand', brand);
        url.searchParams.set('tenant', tenant);
        url.searchParams.set('currency', currency);
        url.searchParams.set('lang', lang);
        url.searchParams.set('identifier', identifier);
        url.searchParams.set('travelcontext', travelcontext || 'DESTINATIONS');

        try {
            const response = await fetch(url.toString());

            if (!response.ok) {
                // Try to parse error response
                let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
                try {
                    const errorData: ApiResponse = await response.json();
                    if (errorData.error) {
                        errorMessage = errorData.error;
                        if (errorData.missing && errorData.missing.length > 0) {
                            errorMessage += ` (Missing parameters: ${errorData.missing.join(', ')})`;
                        }
                        if (errorData.invalid && errorData.invalid.length > 0) {
                            errorMessage += ` (Invalid parameters: ${errorData.invalid.join(', ')})`;
                        }
                    }
                } catch {
                    // Ignore JSON parse errors for error response
                }
                throw new Error(errorMessage);
            }

            const data: ApiResponse = await response.json();

            // Backend returns results:0 without faqs when no FAQs exist (omitempty) – that's valid.
            // But if faqs exists and is not an array, the response is malformed.
            if (data.faqs !== undefined && !Array.isArray(data.faqs)) {
                throw new Error('FAQ data is missing or invalid format.');
            }
            const faqs = Array.isArray(data.faqs) ? data.faqs : [];

            // Map API response to internal format
            const faqGroups: FaqGroup[] = [
                {
                    groupId: 'default-group',
                    groupTitle: data.title,
                    intro: data.intro,
                    groupOrder: 1,
                    faqs: faqs.map((faq, index): FaqEntry => ({
                        id: faq.id,
                        question: faq.question,
                        answer: faq.answer,
                        order: index + 1,
                        isActive: true,
                        intent: 'all' as FaqIntent,
                    })),
                },
            ];

            return {
                identifier,
                brand,
                tenant,
                language: lang,
                currency,
                title: data.title,
                intro: data.intro,
                faqGroups,
            };
        } catch (error) {
            console.error('❌ [FaqService] Error:', error);
            if (error instanceof Error) {
                throw error;
            }
            throw new Error('Failed to fetch FAQ data', { cause: error });
        }
    }
}
